<template>
  <div class="project-planning">
    <!-- Loading -->
    <div v-if="loading" class="planning-loading">
      <div class="loading-spinner"></div>
      <p>{{ $t('common.loading') }}</p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="planning-error">
      <p>{{ error }}</p>
    </div>

    <!-- No data at all -->
    <div v-else-if="phases.length === 0 && tasks.length === 0" class="planning-empty">
      <p>{{ $t('projects.planning.noPhases') }}</p>
    </div>

    <!-- Tabbed Gantt layout -->
    <div v-else class="planning-tabs">

      <!-- Tab bar -->
      <div class="tab-bar" role="tablist">
        <button
          v-if="phases.length > 0"
          role="tab"
          class="tab-btn"
          :class="{ 'tab-btn--active': activeTab === 'phases' }"
          :aria-selected="activeTab === 'phases'"
          @click="activeTab = 'phases'"
        >
          {{ $t('projects.planning.gantt.phasesTab') }}
        </button>

        <button
          v-for="phase in phases"
          :key="phase.id"
          role="tab"
          class="tab-btn"
          :class="{ 'tab-btn--active': activeTab === `phase-${phase.id}` }"
          :aria-selected="activeTab === `phase-${phase.id}`"
          :title="phase.name"
          @click="activeTab = `phase-${phase.id}`"
        >
          <span class="tab-btn__badge" :class="`tab-btn__badge--${statusClass(phase.status)}`"></span>
          <span class="tab-btn__label">{{ phase.name }}</span>
        </button>

        <button
          v-if="phases.length === 0"
          role="tab"
          class="tab-btn tab-btn--active"
          aria-selected="true"
        >
          {{ $t('projects.planning.gantt.tasksTab') }}
        </button>
      </div>

      <!-- ── Phases overview panel ── -->
      <div v-if="activeTab === 'phases'" class="tab-panel" role="tabpanel">
        <div v-if="phaseGantt.rows.length === 0" class="planning-empty">
          <p>{{ $t('projects.planning.gantt.noData') }}</p>
        </div>
        <div v-else class="gantt-wrap">
          <GGanttChart
            :chart-start="phaseGantt.chartStart"
            :chart-end="phaseGantt.chartEnd"
            precision="month"
            bar-start="myStart"
            bar-end="myEnd"
            :push-on-overlap="false"
            :label-column-title="$t('projects.planning.gantt.phasesTab')"
            label-column-width="280px"
            :row-height="52"
            :grid="true"
          >
            <template #timeunit="{ date }">{{ formatMonthLabel(date) }}</template>
            <GGanttRow
              v-for="row in phaseGantt.rows"
              :key="row.id"
              :label="row.label"
              :bars="row.bars"
            />
          </GGanttChart>
        </div>
      </div>

      <!-- ── Per-phase task panels ── -->
      <template v-for="phase in phases" :key="phase.id">
        <div
          v-if="activeTab === `phase-${phase.id}`"
          class="tab-panel"
          role="tabpanel"
        >
          <div v-if="!taskGanttMap[phase.id] || taskGanttMap[phase.id].rows.length === 0" class="planning-empty">
            <p>{{ $t('projects.planning.gantt.noTasksForPhase') }}</p>
          </div>
          <div v-else class="gantt-wrap">
            <GGanttChart
              :chart-start="taskGanttMap[phase.id].chartStart"
              :chart-end="taskGanttMap[phase.id].chartEnd"
              precision="month"
              bar-start="myStart"
              bar-end="myEnd"
              :push-on-overlap="false"
              :label-column-title="$t('projects.planning.gantt.taskColumn')"
              label-column-width="280px"
              :row-height="48"
              :grid="true"
            >
              <template #timeunit="{ date }">{{ formatMonthLabel(date) }}</template>
              <GGanttRow
                v-for="row in taskGanttMap[phase.id].rows"
                :key="row.id"
                :label="row.label"
                :bars="row.bars"
              />
            </GGanttChart>
          </div>
        </div>
      </template>

      <!-- ── Fallback tasks panel (no phases) ── -->
      <div v-if="phases.length === 0" class="tab-panel" role="tabpanel">
        <div v-if="taskOnlyGantt.rows.length === 0" class="planning-empty">
          <p>{{ $t('projects.planning.noPhases') }}</p>
        </div>
        <div v-else class="gantt-wrap">
          <GGanttChart
            :chart-start="taskOnlyGantt.chartStart"
            :chart-end="taskOnlyGantt.chartEnd"
            precision="month"
            bar-start="myStart"
            bar-end="myEnd"
            :push-on-overlap="false"
            :label-column-title="$t('projects.planning.gantt.taskColumn')"
            label-column-width="280px"
            :row-height="48"
            :grid="true"
          >
            <template #timeunit="{ date }">{{ formatMonthLabel(date) }}</template>
            <GGanttRow
              v-for="row in taskOnlyGantt.rows"
              :key="row.id"
              :label="row.label"
              :bars="row.bars"
            />
          </GGanttChart>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { GGanttChart, GGanttRow } from '@infectoone/vue-ganttastic';
import projectPhasesService from '@/services/projectPhases.service';
import projectTasksService from '@/services/projectTasks.service';

interface PhaseItem {
  id: number;
  name: string;
  plannedStartDate?: string | null;
  plannedEndDate?: string | null;
  status?: string | null;
}

interface TaskItem {
  id: number;
  name: string;
  startDate?: string | null;
  endDate?: string | null;
  phaseId?: number | null;
  status?: string | null;
}

interface GanttBar {
  myStart: string;
  myEnd: string;
  ganttBarConfig: {
    id: string;
    label: string;
    style: Record<string, string>;
  };
}

interface GanttRow {
  id: string;
  label: string;
  bars: GanttBar[];
}

interface GanttData {
  rows: GanttRow[];
  chartStart: string;
  chartEnd: string;
}

interface Props {
  projectId: number | string;
  project?: any;
}

const props = defineProps<Props>();

const loading = ref(true);
const error = ref<string | null>(null);
const phases = ref<PhaseItem[]>([]);
const tasks = ref<TaskItem[]>([]);
const activeTab = ref<string>('phases');

// ── helpers ───────────────────────────────────────────────────────────────────

function formatMonthLabel(date: Date): string {
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yy = String(date.getFullYear()).slice(-2);
  return `${mm}/${yy}`;
}

function statusClass(status?: string | null): string {
  if (!status) return 'not_started';
  return status.toLowerCase();
}

function statusColor(status?: string | null): string {
  switch (status?.toUpperCase()) {
    case 'COMPLETED': return '#4CAF50';
    case 'IN_PROGRESS': return '#2196F3';
    case 'ON_HOLD':     return '#FF9800';
    case 'CANCELLED':   return '#9E9E9E';
    default:            return '#90CAF9';
  }
}

function toGanttDate(dateStr: string | null | undefined, endOfDay = false): string | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  const yyyy = d.getFullYear();
  const mm   = String(d.getMonth() + 1).padStart(2, '0');
  const dd   = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd} ${endOfDay ? '23:59' : '00:00'}`;
}

function fmt(d: Date, time: string): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${time}`;
}

function buildChartBounds(rows: GanttRow[]): { chartStart: string; chartEnd: string } {
  const allDates: Date[] = [];
  for (const row of rows) {
    for (const bar of row.bars) {
      const s = new Date(bar.myStart.replace(' ', 'T'));
      const e = new Date(bar.myEnd.replace(' ', 'T'));
      if (!isNaN(s.getTime())) allDates.push(s);
      if (!isNaN(e.getTime())) allDates.push(e);
    }
  }
  if (allDates.length === 0) {
    const today = new Date();
    return {
      chartStart: fmt(new Date(today.getFullYear(), today.getMonth(), 1), '00:00'),
      chartEnd:   fmt(new Date(today.getFullYear(), today.getMonth() + 2, 1), '00:00'),
    };
  }
  const minDate = new Date(Math.min(...allDates.map(d => d.getTime())));
  const maxDate = new Date(Math.max(...allDates.map(d => d.getTime())));
  const startBound = new Date(minDate); startBound.setDate(startBound.getDate() - 1);
  const endBound   = new Date(maxDate); endBound.setDate(endBound.getDate() + 1);
  return { chartStart: fmt(startBound, '00:00'), chartEnd: fmt(endBound, '00:00') };
}

function makePhaseRow(phase: PhaseItem): GanttRow {
  const bars: GanttBar[] = [];
  const start = toGanttDate(phase.plannedStartDate, false);
  const end   = toGanttDate(phase.plannedEndDate, true);
  if (start && end) {
    bars.push({ myStart: start, myEnd: end, ganttBarConfig: { id: `phase-${phase.id}`, label: phase.name, style: { background: statusColor(phase.status), borderRadius: '6px', color: '#fff', fontWeight: '600', boxShadow: '0 2px 6px rgba(0,0,0,0.25)' } } });
  }
  return { id: `phase-row-${phase.id}`, label: phase.name, bars };
}

function makeTaskRow(task: TaskItem): GanttRow {
  const bars: GanttBar[] = [];
  const start = toGanttDate(task.startDate, false);
  const end   = toGanttDate(task.endDate, true);
  if (start && end) {
    bars.push({ myStart: start, myEnd: end, ganttBarConfig: { id: `task-${task.id}`, label: task.name, style: { background: statusColor(task.status), borderRadius: '5px', color: '#fff', fontWeight: '500', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' } } });
  }
  return { id: `task-row-${task.id}`, label: task.name, bars };
}

function normaliseArray<T>(result: any): T[] {
  if (Array.isArray(result)) return result as T[];
  if (Array.isArray(result?.data)) return result.data as T[];
  if (Array.isArray(result?.content)) return result.content as T[];
  return [];
}

// ── computed gantt data per tab ───────────────────────────────────────────────

const phaseGantt = computed<GanttData>(() => {
  const rows = phases.value.map(makePhaseRow);
  return { rows, ...buildChartBounds(rows) };
});

const taskGanttMap = computed<Record<number, GanttData>>(() => {
  const map: Record<number, GanttData> = {};
  for (const phase of phases.value) {
    const rows = tasks.value.filter((t: TaskItem) => t.phaseId === phase.id).map(makeTaskRow);
    map[phase.id] = { rows, ...buildChartBounds(rows) };
  }
  return map;
});

const taskOnlyGantt = computed<GanttData>(() => {
  const rows = tasks.value.map(makeTaskRow);
  return { rows, ...buildChartBounds(rows) };
});

// ── data loading ──────────────────────────────────────────────────────────────

onMounted(async () => {
  loading.value = true;
  error.value = null;
  try {
    const projectIdStr = String(props.projectId);
    if (props.project?.multiPhase) {
      const [phasesResult, tasksResult] = await Promise.all([
        projectPhasesService.getPhases(projectIdStr),
        projectTasksService.getTasks(projectIdStr),
      ]);
      phases.value = normaliseArray<PhaseItem>(phasesResult);
      tasks.value  = normaliseArray<TaskItem>(tasksResult);
    } else {
      const tasksResult = await projectTasksService.getTasks(projectIdStr);
      tasks.value  = normaliseArray<TaskItem>(tasksResult);
      phases.value = [];
    }
    activeTab.value = phases.value.length > 0 ? 'phases' : 'tasks';
  } catch (err: any) {
    error.value = err?.message ?? 'Failed to load planning data';
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
.project-planning {
  height: 100%;
  min-height: 300px;
  display: flex;
  flex-direction: column;
}

/* ── States ─────────────────────────────────────────────────────────────────── */
.planning-loading,
.planning-error,
.planning-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  text-align: center;
  color: var(--text-secondary, #6b7280);
  gap: 0.5rem;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--neutral-200, #e5e7eb);
  border-left-color: var(--primary, #667eea);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ── Tabbed layout ───────────────────────────────────────────────────────────── */
.planning-tabs {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.tab-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  padding: 0 1rem;
  background: var(--surface-secondary, #f8f9fa);
  border-bottom: 2px solid var(--border-color, #e5e7eb);
  flex-shrink: 0;
}

.tab-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border: none;
  background: transparent;
  color: var(--text-secondary, #6b7280);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  transition: color 0.15s, border-color 0.15s;
  white-space: nowrap;
  max-width: 200px;
}

.tab-btn:hover {
  color: var(--primary, #667eea);
  background: var(--surface-hover, rgba(102, 126, 234, 0.06));
  border-radius: 4px 4px 0 0;
}

.tab-btn--active {
  color: var(--primary, #667eea);
  border-bottom-color: var(--primary, #667eea);
  font-weight: 600;
}

.tab-btn__label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tab-btn__badge {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.tab-btn__badge--completed    { background: #4CAF50; }
.tab-btn__badge--in_progress  { background: #2196F3; }
.tab-btn__badge--on_hold      { background: #FF9800; }
.tab-btn__badge--cancelled    { background: #9E9E9E; }
.tab-btn__badge--not_started  { background: #90CAF9; }

/* Tab panel */
.tab-panel {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 1rem;
}

/* ── Gantt chart container ───────────────────────────────────────────────────── */
.gantt-wrap {
  background: white;
  border: 1px solid var(--border-color, #e5e7eb);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.08);
  min-height: 200px;
}

/* ── Left label column (GGanttLabelColumn) ──────────────────────────────────── */
.gantt-wrap :deep(.g-label-column) {
  border-right: 2px solid var(--border-color, #e5e7eb);
  flex-shrink: 0;
}

/* Column header title ("Phase / Tâche") */
.gantt-wrap :deep(.g-label-column-header) {
  display: flex !important;
  align-items: center !important;
  padding: 0 12px !important;
  font-weight: 700 !important;
  font-size: 0.82rem !important;
  letter-spacing: 0.02em;
  white-space: nowrap !important;
  overflow: hidden !important;
  background: var(--surface-secondary, #f0f2f5) !important;
  border-bottom: 2px solid var(--border-color, #e5e7eb) !important;
  color: var(--text-primary, #374151) !important;
}

/* Each label row cell in the left column */
.gantt-wrap :deep(.g-label-column-row) {
  display: flex !important;
  align-items: center !important;
  padding: 0 12px !important;
  overflow: hidden !important;
  border-bottom: 1px solid var(--border-color, #f0f2f5) !important;
  box-sizing: border-box;
}

/* Zebra striping — override library's ternary/quartenary colors */
.gantt-wrap :deep(.g-label-column-rows > div:nth-child(odd)) {
  background: var(--surface-secondary, #f8f9fa) !important;
}
.gantt-wrap :deep(.g-label-column-rows > div:nth-child(even)) {
  background: #fff !important;
}

/* The span text inside each label row — truncate with ellipsis */
.gantt-wrap :deep(.g-label-column-row span) {
  overflow: hidden !important;
  white-space: nowrap !important;
  text-overflow: ellipsis !important;
  min-width: 0 !important;
  flex: 1 !important;
  font-size: 0.875rem !important;
  font-weight: 500 !important;
  color: var(--text-primary, #1f2937) !important;
}

/* ── Bar label text (inside the Gantt bars) ─────────────────────────────────── */
.gantt-wrap :deep(.g-gantt-bar-label) {
  display: flex !important;
  align-items: center !important;
  height: 100%;
  padding: 0 8px;
  overflow: hidden;
}

.gantt-wrap :deep(.g-gantt-bar-label > div) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  font-size: 0.78rem;
  font-weight: 600;
  color: #fff;
}

/* ── Time axis header ───────────────────────────────────────────────────────── */
.gantt-wrap :deep(.g-timeaxis) {
  border-bottom: 2px solid var(--border-color, #e5e7eb);
}

/* Upper time units (year row) */
.gantt-wrap :deep(.g-upper-timeunit) {
  font-weight: 700 !important;
  font-size: 0.8rem !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  letter-spacing: 0.03em;
  border-right: 1px solid rgba(0, 0, 0, 0.08);
}

/* Lower time units (month row) */
.gantt-wrap :deep(.g-timeunit) {
  font-size: 0.72rem !important;
  font-weight: 500 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  overflow: hidden !important;
  border-right: 1px solid rgba(0, 0, 0, 0.06);
  text-align: center;
}
</style>
